import CloseIcon from '@mui/icons-material/Close'
import { Box, Fab, Tooltip, Typography } from '@mui/material'
import { useOnboarding } from '@src/providers/OnboardingProvider'
import { useCallback, useEffect, useState, type JSX, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { StepTarget } from 'react-joyride'

interface Hit {
  rect: DOMRect
  title: ReactNode
  content: ReactNode
}

const resolveTarget = (target: StepTarget): HTMLElement | null => {
  if (typeof target === 'string') {
    return document.querySelector<HTMLElement>(target)
  }
  if (typeof target === 'function') {
    const result = (target as () => HTMLElement | null)()
    return result instanceof HTMLElement ? result : null
  }
  if (target instanceof HTMLElement) return target
  return null
}

const HelpOverlay = (): JSX.Element | null => {
  const { isHelpMode, stopHelp, allFeatureSteps } = useOnboarding()
  const { t } = useTranslation()
  const [hits, setHits] = useState<Hit[]>([])

  const measure = useCallback(() => {
    const found: Hit[] = []
    for (const step of allFeatureSteps) {
      const el = resolveTarget(step.target)
      if (!el) continue
      const rect = el.getBoundingClientRect()
      if (rect.width === 0 && rect.height === 0) continue
      found.push({ rect, title: step.title ?? null, content: step.content ?? null })
    }
    setHits(found)
  }, [allFeatureSteps])

  useEffect(() => {
    if (!isHelpMode) return
    measure()
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stopHelp()
    }
    const onChange = () => measure()
    window.addEventListener('keydown', onEsc)
    window.addEventListener('resize', onChange)
    window.addEventListener('scroll', onChange, true)
    return () => {
      window.removeEventListener('keydown', onEsc)
      window.removeEventListener('resize', onChange)
      window.removeEventListener('scroll', onChange, true)
    }
  }, [isHelpMode, measure, stopHelp])

  if (!isHelpMode) return null

  return (
    <>
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0, 0, 0, 0.25)', zIndex: 1300, pointerEvents: 'auto' }}
      />
      {hits.map((hit, i) => (
        <Tooltip
          key={i}
          arrow
          title={
            <Box sx={{ p: 0.5 }}>
              {hit.title ? (
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {hit.title}
                </Typography>
              ) : null}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {hit.content}
              </Typography>
            </Box>
          }
          slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
        >
          <Box
            sx={{
              position: 'fixed',
              top: hit.rect.top,
              left: hit.rect.left,
              width: hit.rect.width,
              height: hit.rect.height,
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              boxShadow: '0 0 0 3px rgba(255, 255, 255, 0.6)',
              zIndex: 1301,
              cursor: 'help',
              pointerEvents: 'auto',
            }}
          />
        </Tooltip>
      ))}
      <Fab
        size="small"
        color="primary"
        onClick={stopHelp}
        aria-label={t('tour.help.closeAria')}
        sx={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1302 }}
      >
        <CloseIcon />
      </Fab>
    </>
  )
}

export default HelpOverlay
